/**
 * @engine/core - Render Pipeline (Scriptable Render Passes)
 *
 * A scriptable, ordered list of {@link ViewportPass}es executed each frame by a viewport renderer. Replaces the
 * monolithic, duplicated `onRender` orchestration in EditorViewRenderer / GameViewRenderer: editor and game
 * differ only by *which passes* they register (gizmos vs post-process) — the shared content/lighting passes
 * live once.
 *
 * The executor wraps every pass in `Profiler.begin/end`, so the profiler tree is a property of the pass list —
 * identical for both viewports, with no per-renderer marker wiring (this fixes the "GameView is dark in the
 * profiler" problem structurally).
 *
 * Naming note: this is the *viewport-level* pass system. render2d owns a separate low-level `core/RenderPass.ts`
 * (GPU-pass primitives) and GPU-resource classes (`LightCullPass`/`ShadowMapPass`/`VFXLightInjectPass`) — hence
 * `ViewportPass` here, and verb-noun pass names (`CullLightsPass`) for the wrappers that drive those resources.
 *
 * See `.refactor/SCRIPTABLE-RENDER-PIPELINE-DESIGN.md`.
 *
 * @module @engine/core
 */

import { Profiler } from '../Profiler';
import type {
  IRenderContext,
  Camera2D,
  TextureHandle,
  BufferHandle,
  LightManager,
  DeferredLightingPipeline,
  ShadowCaster2D,
  LightEmittingSystem,
} from '@engine/render2d';
import type { Level } from '../Level';
import type { SceneSpriteCollector, SpriteCollectorDeps } from './scene/SceneSpriteCollector';

/**
 * Scene callbacks the shared passes invoke (injected by the host/subsystem). These are the editor↔player seam
 * for collecting scene-side data the renderer package itself doesn't own.
 */
export interface ViewportRenderCallbacks {
  /** Collect active shadow-caster shapes from the scene (for the shadow compute pass). */
  shadowCasterCollect?: (() => ShadowCaster2D[]) | null;
  /** Collect light-emitting particle systems (for the VFX light-injection compute pass). */
  vfxLightInject?: (() => LightEmittingSystem[]) | null;
  /** Render VFX particles with the given view-projection into the given context. */
  vfxRender?: ((ctx: IRenderContext, viewProjection: Float32Array, isHdrTarget: boolean) => void) | null;
  /** True while play mode is active (gates particle rendering, matching the legacy `PlayMode.instance.isPlaying`). */
  isPlaying?: boolean;
}

/**
 * When a pass runs. One linear order serves all three render paths (deferred / forward+ / unlit) because the
 * lighting compute (collect/inject/cull/shadows) is framebuffer-independent and can run before opaque geometry.
 * Numeric gaps leave room to slot custom passes between stages via {@link ViewportPass.order}.
 */
export enum RenderStage {
  /** Choose color target, clear, set viewport. */
  Setup = 0,
  /** Compute only: collect lights → inject VFX lights → cull (Forward+) → shadows. No color writes. */
  Lighting = 100,
  /** Opaque geometry — deferred: → GBuffer MRT; forward+: lit → color; unlit: → color. */
  Opaque = 200,
  /** Deferred only: fullscreen GBuffer + lights → color. */
  Accumulation = 300,
  /** Volumetric composite, decals, … (after the opaque color resolve). */
  AfterOpaqueColor = 400,
  /** Forward-lit transparent sprites → color. */
  Transparent = 500,
  /** VFX particles. */
  AfterTransparent = 600,
  /** Grid, gizmos, selection — editor overlays. */
  Overlay = 700,
  /** Tonemap / bloom / post stack — game. */
  PostProcess = 800,
  /** Game UI (screen-space canvases) — LDR, after the HDR resolve, under editor overlays (W-UI). */
  UI = 850,
  /** LDR overlays drawn onto the backbuffer AFTER the HDR resolve (editor gizmos/selection — must not be tonemapped). */
  AfterPostProcess = 900,
}

/**
 * Deferred lighting resources resolved for the current frame (mirrors the renderers' deferredLightingCallback
 * result). Null when no lighting subsystem / deferred path is available.
 */
export interface LightingResources {
  pipeline: DeferredLightingPipeline;
  lightManager: LightManager;
  collect: (camera: Camera2D, width: number, height: number) => void;
  iesProfileBuffer?: BufferHandle | null;
}

/**
 * Per-frame state handed to every pass. Built once by the renderer and reused across the pass list.
 *
 * Cross-pass GPU handles (e.g. the GBuffer depth texture, the Forward+ cull pass instance) are stashed on the
 * {@link blackboard} by the producing pass and read by consumers — keeping the context itself lean and the
 * pass graph decoupled.
 */
export interface ViewportPassContext {
  ctx: IRenderContext;
  camera: Camera2D;
  level: Level | null;
  width: number;
  height: number;
  /** Seconds (animation clock). */
  time: number;
  /** Seconds since last frame. */
  deltaTime: number;
  isPlayMode: boolean;
  /** Whether the active color target is HDR (rgba16float offscreen) vs the LDR swap chain. */
  isHdrTarget: boolean;
  /** Active color render target; null = swap-chain backbuffer. */
  colorTarget: TextureHandle | null;
  /** Shared sprite collection (collected once per frame by the Opaque-stage sprite pass). */
  sprites: SceneSpriteCollector;
  /** Collaborators the sprite collector needs (texture/material resolvers — the editor↔player seam). */
  spriteDeps: SpriteCollectorDeps;
  /** Deferred lighting resources, or null when unavailable. */
  lighting: LightingResources | null;
  /** Scene-side collect/render callbacks (shadow casters, VFX lights, particle render). */
  callbacks: ViewportRenderCallbacks;
  /**
   * Bind the active color target (backbuffer or HDR offscreen) + viewport, resetting the WebGPU scissor.
   * Color-drawing passes call this at the start of `execute` so they're robust to whatever target a prior
   * pass (GBuffer MRT, volumetric buffer) left bound.
   */
  beginColorPass(): void;
  /** Inter-pass handle store (e.g. 'gbuffer.depth', 'forwardPlus.lightCull'). */
  blackboard: Map<string, unknown>;
}

/** Blackboard keys shared between passes (producers set, consumers read). */
export const BB = {
  /** GBuffer depth texture (set by the GBuffer pass, read by volumetric). */
  gbufferDepth: 'gbuffer.depth',
  /** The Forward+ {@link LightCullPass} instance (set by the cull pass). */
  lightCull: 'forwardPlus.lightCull',
  /** The {@link ShadowMapPass} instance (set by the shadow pass). */
  shadowMap: 'forwardPlus.shadowMap',
} as const;

/**
 * A single scriptable render pass. Subclasses declare their {@link stage} (+ optional {@link order} tiebreak)
 * and implement {@link execute}. Lazy GPU resources are created in {@link init} and freed in {@link dispose}.
 */
export abstract class ViewportPass {
  /** The stage this pass runs in. */
  abstract readonly stage: RenderStage;
  /** Tiebreak within a stage (lower runs first). */
  order = 0;
  /** Toggle without removing from the list. */
  enabled = true;
  /** Profiler label, e.g. 'Render.CullLights'. */
  abstract readonly profileName: string;

  /** Optional one-time GPU resource setup (called by the renderer once the context exists). */
  init?(ctx: IRenderContext): void;

  /** Execute the pass for this frame. */
  abstract execute(c: ViewportPassContext): void;

  /** Optional GPU resource teardown. */
  dispose?(): void;
}

/**
 * A pass whose body is a closure — for viewport-specific steps (editor grid/gizmos/volumetric) that wrap
 * private renderer methods without needing a dedicated class. Shared, reusable steps get their own class.
 */
export class CallbackPass extends ViewportPass {
  readonly stage: RenderStage;
  readonly profileName: string;
  private readonly fn: (c: ViewportPassContext) => void;

  constructor(stage: RenderStage, profileName: string, fn: (c: ViewportPassContext) => void, order = 0) {
    super();
    this.stage = stage;
    this.profileName = profileName;
    this.fn = fn;
    this.order = order;
  }

  execute(c: ViewportPassContext): void {
    this.fn(c);
  }
}

/**
 * Ordered executor for a {@link ViewportPass} list. Sorts by (stage, order) — stably, so registration order
 * breaks ties — and runs each enabled pass wrapped in a Profiler section.
 */
export class RenderPipeline {
  private passes: ViewportPass[] = [];
  private dirty = false;

  /** Register one or more passes. Re-sorts lazily on next {@link execute}. */
  add(...passes: ViewportPass[]): this {
    for (const p of passes) this.passes.push(p);
    this.dirty = true;
    return this;
  }

  /** Initialize every pass's lazy GPU resources against the given context. */
  init(ctx: IRenderContext): void {
    for (const p of this.passes) p.init?.(ctx);
  }

  /** All registered passes in execution order (sorted). */
  get ordered(): readonly ViewportPass[] {
    if (this.dirty) this.sort();
    return this.passes;
  }

  private sort(): void {
    // Stable sort by (stage, order); registration index breaks remaining ties.
    this.passes = this.passes
      .map((p, i) => ({ p, i }))
      .sort((a, b) => (a.p.stage - b.p.stage) || (a.p.order - b.p.order) || (a.i - b.i))
      .map((e) => e.p);
    this.dirty = false;
  }

  execute(c: ViewportPassContext): void {
    if (this.dirty) this.sort();
    for (const p of this.passes) {
      if (!p.enabled) continue;
      Profiler.begin(p.profileName);
      c.ctx.pushGpuMarker?.(p.profileName);
      try {
        p.execute(c);
      } finally {
        c.ctx.popGpuMarker?.();
        Profiler.end(p.profileName);
      }
    }
  }

  dispose(): void {
    for (const p of this.passes) p.dispose?.();
    this.passes = [];
  }
}
